import { AutoEnrollmentTypes } from '@/AutoEnrollment/domain'
import { CreateProfileFormValues, UpdateProfileData } from '@/partnersGroups/domain/ProfileTypes'
import { HttpMethod, HttpModel, MetaModel, edenredProducts } from '@/Shared/domain'
import { axiosFetch } from '@/Shared/infrastructure'
import {
  changeEmail,
  changePassword,
  createProfile,
  deleteAccount,
  deleteProfile,
  getPermissions,
  getUserData,
  multiWeb,
  setActiveMultiWeb,
  updateProfile,
} from '@/Shared/infrastructure/endpoints'
import {
  DeleteAccountDataForm,
  EmailChangeDataForm,
  MerchantProductModel,
  MerchantsDataModel,
  MultiWebClientMerchantModel,
  PasswordChangeDataForm,
  UserModel,
  UserRepository,
  UserType,
} from '@/User/domain'

export function createApiUserRepository(): UserRepository {
  return new ApiUserRepository()
}

export function getUserType(response: ApiUserData): UserType {
  if (
    response.data.merchants.some(merchant =>
      merchant.productsInfo.some(
        product => product.productCode === edenredProducts.ticketRestaurant,
      ),
    )
  ) {
    return UserType.RESTAURANT
  }

  if (
    response.data.merchants.some(merchant =>
      merchant.productsInfo.some(
        product => product.productCode === edenredProducts.ticketGuarderia,
      ),
    )
  ) {
    return UserType.NURSERY
  }

  return UserType.RESTAURANT
}

type ApiProductsInfoModel = {
  productCode: edenredProducts
  productName: string
  amendmentId: number
  amendmentStartDate: string
  pamCode: number
  paymentModeName: string
  contractOpenDate: string
  canBeCharged: boolean
  canBeChargedForUnknownTerminals: boolean
  feesAppliedAllways: boolean
  invoiceMinAmount: number
}

type ApiMerchantModel = {
  id: number
  parentId: number | null
  parentName: string | null
  parentDependency: boolean
  masterGroupId: number | null
  masterGroupAlias: string | null
  superMasterGroupAlias: string | null
  name: string
  merchantBusinessName: string
  document: string
  roadType: string
  streetName: string
  streetNumber: string
  adressAditionalInfo1: null
  adressAditionalInfo2: null
  zipCode: string
  town: string
  adressDepartment: string
  adressTelephone: string
  hasFlatRateCommission: boolean
  isMasterGroup: boolean
  isParent: boolean
  isParentCommercial: boolean
  isParentWithHardDependency: boolean
  isChild: boolean
  isChildCommercial: boolean
  isChildWithHardDependency: boolean
  isIndependent: boolean
  isNursery: boolean
  productsInfo: ApiProductsInfoModel[]
}

type ApiHirableSubProductsModel = {
  id: number
  name: string
  isOptional: boolean
}

type ApiHirableProductsModel = {
  productCode: edenredProducts
  productName: string
  productHiringOptional: boolean
  subProducts: ApiHirableSubProductsModel[]
}

type ApiHirableProductsInfoModel = {
  hasHirableProducts: boolean
  canBeHiredOnline: boolean
  hirableProducts: ApiHirableProductsModel[]
}

type ApiUserModel = {
  name: string
  surname: string
  surname2: string | null
  email: string
  userName: string
  autoEnrollmentType: AutoEnrollmentTypes
}

type ApiUserDataModel = {
  merchants: ApiMerchantModel[]
  hirableProductsInfo: ApiHirableProductsInfoModel | null
  user: ApiUserModel
}

type ApiUserData = {
  data: ApiUserDataModel
  meta: MetaModel
}

type ApiMultiWebClientSubItemModel = {
  code: number
  name: string
  cif: string
}

type ApiMultiWebClientMerchantModel = {
  code: number
  name: string
  cif: string
  isIndependent: boolean
  isParent: boolean
  isParentWithHardDependency: boolean
  isParentCommercial: boolean
  isMasterGroup: boolean
  subItems: ApiMultiWebClientSubItemModel[]
}

type ApiSetActiveMultiWebRequest = {
  activeMerchantId: number
}

type ApiCreateProfileModel = {
  name: string;
  surname: string;
  surname2?: string;
  email: string;
  rolName: string;
}

const convertUserDataToDomain = (response: ApiUserData): UserModel => {
  const model: UserModel = {
    user: {
      name: response.data.user.name,
      surname: response.data.user.surname,
      surname2: response.data.user.surname2 || '',
      email: response.data.user.email,
      userType: getUserType(response),
      userName: response.data.user.userName,
      autoEnrollmentType:
        response.data.user?.autoEnrollmentType ?? AutoEnrollmentTypes.defualtProcessType,
    },
    merchants: response.data.merchants.map(
      (merchant: ApiMerchantModel): MerchantsDataModel => ({
        id: merchant.id,
        name: merchant.name,
        businessName: merchant.merchantBusinessName,
        document: merchant.document,
        hasFlatRateCommission: merchant.hasFlatRateCommission,
        isChildCommercial: merchant.isChildCommercial,
        isChildWithHardDependency: merchant.isChildWithHardDependency,
        isParent: merchant.isParent,
        isParentCommercial: merchant.isParentCommercial,
        isParentWithHardDependency: merchant.isParentWithHardDependency,
        isChild: merchant.isChild,
        isMasterGroup: merchant.isMasterGroup,
        isIndependent: merchant.isIndependent,
        isNursery: merchant.isNursery,
        products: merchant.productsInfo.map(
          (product: ApiProductsInfoModel): MerchantProductModel => ({
            productCode: product.productCode,
            productName: product.productName,
            amendmentId: product.amendmentId,
            amendmentStartDate: product.amendmentStartDate,
            pamCode: product.pamCode,
            paymentModeName: product.paymentModeName,
            contractOpenDate: product.contractOpenDate,
            canBeCharged: product.canBeCharged,
            canBeChargedForUnknownTerminals: product.canBeChargedForUnknownTerminals,
            feesAppliedAllways: product.feesAppliedAllways,
            invoiceMinAmount: product.invoiceMinAmount,
          }),
        ),
      }),
    ),
    hirableProductsInfo: {
      hasHirableProducts: response.data?.hirableProductsInfo?.hasHirableProducts ?? false,
      canBeHiredOnline: response.data?.hirableProductsInfo?.canBeHiredOnline ?? false,
      hirableProducts:
        response.data?.hirableProductsInfo?.hirableProducts?.map(hirableProduct => ({
          productCode: hirableProduct.productCode,
          productName: hirableProduct.productName,
          productHiringOptional: hirableProduct.productHiringOptional,
          subProducts:
            hirableProduct.subProducts?.map(subProduct => ({
              id: subProduct.id,
              name: subProduct.name,
              isOptional: subProduct.isOptional,
            })) ?? [],
        })) ?? [],
    },
  }
  return model
}

const convertApiToMultiWebClientMerchant = (
  apiMerchant: ApiMultiWebClientMerchantModel,
): MultiWebClientMerchantModel => {
  return {
    code: apiMerchant.code,
    name: apiMerchant.name,
    cif: apiMerchant.cif,
    isIndependent: apiMerchant.isIndependent,
    isParent: apiMerchant.isParent,
    isParentWithHardDependency: apiMerchant.isParentWithHardDependency,
    isParentCommercial: apiMerchant.isParentCommercial,
    isMasterGroup: apiMerchant.isMasterGroup,
    subItems: apiMerchant.subItems?.map(subItem => ({
      code: subItem.code,
      name: subItem.name,
      cif: subItem.cif,
    })) || [],
  }
}

class ApiUserRepository implements UserRepository {
  async GetUserData(): Promise<HttpModel<UserModel>> {
    const response = await axiosFetch(getUserData, HttpMethod.GET, {})

    if (!response.data) {
      return {
        data: {
          user: null,
          merchants: [],
          hirableProductsInfo: {
            hasHirableProducts: false,
            canBeHiredOnline: false,
            hirableProducts: [],
          },
        },
        meta: response.meta,
      }
    }
    return {
      data: convertUserDataToDomain(response),
      meta: response.meta,
    }
  }

  async ChangePassword(data: PasswordChangeDataForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(changePassword, HttpMethod.POST, data)

    return {
      data: response?.data,
      meta: response.meta,
    }
  }

  async ChangeEmail(data: EmailChangeDataForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(changeEmail, HttpMethod.POST, data)

    return {
      data: response?.data,
      meta: response.meta,
    }
  }

  async DeleteAccount(account: DeleteAccountDataForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(deleteAccount, HttpMethod.POST, {
      reason: account.reason,
      acceptConditions: account.acceptConditions,
    })
    return response
  }

  async GetMultiWebClientMerchants(): Promise<HttpModel<MultiWebClientMerchantModel[]>> {
    const response = await axiosFetch(multiWeb, HttpMethod.GET, {})

    if (!response.data) {
      return {
        data: [],
        meta: response.meta,
      }
    }

    return {
      data: response.data.map(convertApiToMultiWebClientMerchant),
      meta: response.meta,
    }
  }

  async SetActiveMultiWeb(activeMerchantId: number): Promise<HttpModel<boolean>> {
    const requestBody: ApiSetActiveMultiWebRequest = {
      activeMerchantId
    }

    const response = await axiosFetch(setActiveMultiWeb, HttpMethod.POST, requestBody)
    
    return {
      data: response?.data ?? true,
      meta: response.meta,
    }
  }

  async CreateProfile(data: CreateProfileFormValues): Promise<HttpModel<boolean>> {
     const requestBody: ApiCreateProfileModel = {
      email: data.email ?? "",
      name: data.name ?? "",
      surname: data.surname ?? "",
      surname2: data.surname2 ?? "",
      rolName: data.rolName ?? "",
    }
    
    const response = await axiosFetch(createProfile, HttpMethod.POST, requestBody)
    
    return {
      data: response?.data ?? true,
      meta: response.meta,
    }
  }

  async UpdateProfile(data: UpdateProfileData): Promise<HttpModel<boolean>> {

    const response = await axiosFetch(updateProfile, HttpMethod.POST, data)

    return {
      data: response?.data ?? true,
      meta: response.meta,
    }
  }

  async DeleteProfile(userNameToDelete: string): Promise<HttpModel<boolean>> {

    const response = await axiosFetch(deleteProfile, HttpMethod.DELETE, { userNameToDelete })

    return {
      data: response?.data ?? true,
      meta: response.meta,
    }
  }

  async GetPermissions(): Promise<HttpModel<string[]>> {

    const response = await axiosFetch(getPermissions, HttpMethod.GET, {})

    return {
      data: response?.data ?? true,
      meta: response.meta,
    }
  }
}
